import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { LockKeyhole, AlertCircle, Eye, EyeOff, CheckCircle2, UserRound, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { user: currentUser, logout } = useAuth();
  const [info, setInfo] = useState(null); // { valid, email, email_masked, nome, cognome, reason }

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/reset-password/info`, { params: { token } })
      .then(r => setInfo(r.data))
      .catch(() => setInfo({ valid: false, reason: 'invalid' }));
  }, [token]);

  const otherUserLoggedIn = !!(currentUser && info?.email && currentUser.email !== info.email);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('La password deve avere almeno 6 caratteri'); return; }
    if (password !== confirm) { setError('Le due password non coincidono'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success(`Password impostata per ${info?.nome || 'l\'account'}${info?.email_masked ? ` (${info.email_masked})` : ''}`);
      // Se era collegato un altro utente (es. operatore che ha aperto il link), chiudiamo la sua sessione
      // per evitare confusione: il login successivo sarà con le nuove credenziali del paziente.
      if (currentUser) { try { await logout(); } catch { /* ignore */ } }
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossibile reimpostare la password. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-[var(--shadow-md)]" data-testid="reset-password-card">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-[#238A92] rounded-xl flex items-center justify-center text-white mx-auto mb-2">
            {done ? <CheckCircle2 size={24} /> : <LockKeyhole size={24} />}
          </div>
          <CardTitle className="text-2xl" style={{fontFamily:'Space Grotesk'}}>{done ? 'Password aggiornata' : 'Nuova password'}</CardTitle>
          <CardDescription>{done ? 'Ora puoi accedere con la nuova password. Ti stiamo riportando al login...' : 'Scegli una nuova password per il tuo account'}</CardDescription>
        </CardHeader>
        <CardContent>
          {token && info?.valid && !done && (
            <div className="mb-4 rounded-lg border border-[#238A92]/30 bg-[#238A92]/5 px-3 py-2.5 text-sm flex items-start gap-2" data-testid="reset-password-target">
              <UserRound size={16} className="text-[#238A92] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[#1F2A37]">Stai impostando la password per <strong>{info.nome} {info.cognome}</strong></p>
                <p className="text-xs text-muted-foreground">Account: {info.email_masked}</p>
              </div>
            </div>
          )}
          {otherUserLoggedIn && !done && (
            <Alert className="mb-4 border-amber-300 bg-amber-50 text-amber-900" data-testid="reset-password-other-user-warning">
              <AlertTriangle size={16} className="text-amber-600" />
              <AlertDescription>
                Sei collegato come <strong>{currentUser.email}</strong>. Questo link imposta la password dell'account di <strong>{info.nome}</strong>, non la tua. Al termine verrai scollegato.
              </AlertDescription>
            </Alert>
          )}
          {token && info && !info.valid && !done && (
            <Alert variant="destructive" className="mb-4" data-testid="reset-password-invalid">
              <AlertCircle size={16} />
              <AlertDescription>{info.reason === 'expired' ? 'Questo link è scaduto.' : 'Questo link non è valido o è già stato utilizzato.'} <Link to="/password-dimenticata" className="underline font-medium">Richiedine uno nuovo</Link>.</AlertDescription>
            </Alert>
          )}
          {!token ? (
            <div className="space-y-4" data-testid="reset-password-no-token">
              <Alert variant="destructive">
                <AlertCircle size={16} />
                <AlertDescription>Link non valido: manca il codice di reset. Richiedi un nuovo link.</AlertDescription>
              </Alert>
              <Link to="/password-dimenticata"><Button variant="outline" className="w-full">Richiedi nuovo link</Button></Link>
            </div>
          ) : done ? (
            <Link to="/login"><Button className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="reset-password-login-btn">Vai al login</Button></Link>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" data-testid="reset-password-error">
                  <AlertCircle size={16} />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="rp-password">Nuova password</Label>
                <div className="relative">
                  <Input id="rp-password" type={show ? 'text' : 'password'} placeholder="Almeno 6 caratteri" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} autoFocus className="pr-10" data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShow(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37] transition-colors" aria-label={show ? 'Nascondi password' : 'Mostra password'}>
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-confirm">Conferma password</Label>
                <Input id="rp-confirm" type={show ? 'text' : 'password'} placeholder="Ripeti la password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={6} data-testid="reset-password-confirm-input" />
              </div>
              <Button type="submit" className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white" disabled={loading} data-testid="reset-password-submit-btn">
                {loading ? 'Salvataggio...' : 'Imposta nuova password'}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Link scaduto? <Link to="/password-dimenticata" className="text-[#238A92] hover:underline font-medium">Richiedine uno nuovo</Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
